import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, Shield, Smartphone, CheckCircle2, ChevronDown, ChevronUp, Apple } from 'lucide-react';

// Single source of truth — keep in sync with /public/downloads/android-manifest.json
const APK = {
  version: '1.2.15',
  build: 125,
  size_mb: 93,
  released_at: 'June 15, 2026',
  sha256: '04bbc897e3ad8fb42fb5cad09790685c6ad95f10218c747f421ccf62abae6f0d',
  download_url: '/downloads/kenya-challenge-latest.apk',
  pinned_url: '/downloads/kenya-challenge-v1.2.15-universal.apk',
};

export default function AndroidDownloadPage() {
  const [showWhy, setShowWhy] = useState(false);
  const [showVerify, setShowVerify] = useState(false);

  return (
    <div className="container-app py-8 md:py-14 max-w-2xl" data-testid="android-download-page">
      <div className="flex items-center gap-2 mb-3">
        <Smartphone className="w-5 h-5 text-orange-600" />
        <span className="text-orange-600 font-medium text-xs tracking-wider uppercase">Android</span>
      </div>

      <h1 className="text-3xl md:text-4xl font-bold text-stone-900 leading-tight mb-2">
        Install The Kenya Challenge for Android
      </h1>
      <p className="text-stone-500 text-base md:text-lg mb-8">
        Track your walks automatically. Watch your map of Kenya fill up.
      </p>

      {/* Primary CTA */}
      <Card className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] mb-6">
        <CardContent className="p-6 text-center">
          <a
            href={APK.download_url}
            data-testid="android-download-btn"
            className="inline-flex items-center justify-center gap-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg rounded-2xl py-5 px-6 transition-colors shadow-sm"
          >
            <Download className="w-6 h-6" />
            Download for Android
          </a>
          <p className="text-stone-600 text-sm mt-4 font-medium">
            v{APK.version} &middot; {APK.size_mb} MB
          </p>
          <p className="text-stone-400 text-xs mt-1">
            Last updated {APK.released_at}
          </p>
        </CardContent>
      </Card>

      {/* 3-step install guide */}
      <section className="mb-8" data-testid="android-install-steps">
        <h2 className="text-xl font-bold text-stone-900 mb-4">How to install</h2>
        <ol className="space-y-4">
          <Step
            number={1}
            title="Tap Download"
            body="Your phone saves the file to Downloads."
          />
          <Step
            number={2}
            title="Tap the file"
            body={
              <>
                Android may ask <strong>&ldquo;Allow this browser to install apps?&rdquo;</strong> &mdash; tap{' '}
                <strong>Allow</strong>. You may also see Google Play Protect offer to scan. Either choice
                is safe; <em>&ldquo;Install without scanning&rdquo;</em> continues straight to install.
              </>
            }
          />
          <Step
            number={3}
            title="Open the Kenya Challenge"
            body="Sign in with your kenyachallenge.org email and password. Your team, supporters, and progress are already there."
          />
        </ol>
      </section>

      {/* Why is there a warning? */}
      <Card className="bg-amber-50 border border-amber-100 rounded-2xl mb-6">
        <CardContent className="p-5">
          <button
            type="button"
            onClick={() => setShowWhy(v => !v)}
            className="w-full flex items-center justify-between text-left"
            data-testid="android-why-toggle"
          >
            <span className="flex items-center gap-2 font-semibold text-stone-900">
              <Shield className="w-4 h-4 text-amber-700" />
              Why is there a warning?
            </span>
            {showWhy ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
          </button>
          {showWhy && (
            <div className="mt-3 text-sm text-stone-700 space-y-2 leading-relaxed" data-testid="android-why-body">
              <p>
                The warnings are Android&rsquo;s standard sideload notices because we deliver this app
                directly rather than through Google Play (Play has a 14-day waiting period for new
                developer accounts). It&rsquo;s the same app we&rsquo;ll publish on Play once that window clears.
              </p>
              <p>
                You can independently verify this exact file using the SHA-256 checksum below.
                Kenya Education Fund is also registered with Google&rsquo;s Android Developer Verification
                programme, which keeps a &ldquo;verified developer&rdquo; badge on the install screen.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Privacy note */}
      <Card className="bg-white rounded-2xl border border-stone-100 mb-6">
        <CardContent className="p-5">
          <div className="flex items-start gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <h3 className="font-semibold text-stone-900">Privacy</h3>
          </div>
          <p className="text-sm text-stone-700 leading-relaxed">
            The app only reads your step count and walking distance from Google Health Connect. Nothing
            else. No location, no contacts, no heart rate. Full privacy policy:{' '}
            <Link to="/privacy" className="text-orange-600 hover:text-orange-700 underline" data-testid="android-privacy-link">
              kenyachallenge.org/privacy
            </Link>
            .
          </p>
        </CardContent>
      </Card>

      {/* Verify the download */}
      <Card className="bg-white rounded-2xl border border-stone-100 mb-6">
        <CardContent className="p-5">
          <button
            type="button"
            onClick={() => setShowVerify(v => !v)}
            className="w-full flex items-center justify-between text-left"
            data-testid="android-verify-toggle"
          >
            <span className="font-semibold text-stone-900 text-sm">Verify the download</span>
            {showVerify ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
          </button>
          {showVerify && (
            <div className="mt-3 space-y-3 text-xs text-stone-600 leading-relaxed" data-testid="android-verify-body">
              <p>
                After downloading, compare the file&rsquo;s SHA-256 fingerprint with the value below. On
                most phones you&rsquo;ll need a free file-hash app from the Play Store; on a desktop you can
                run <code className="text-stone-800 bg-stone-100 px-1 py-0.5 rounded">shasum -a 256 kenya-challenge-latest.apk</code>.
              </p>
              <p className="font-mono text-[11px] break-all bg-stone-50 border border-stone-100 rounded-lg p-2 text-stone-700">
                {APK.sha256}
              </p>
              <p>
                Version-pinned URL (never changes):{' '}
                <a href={APK.pinned_url} className="text-orange-600 hover:text-orange-700 underline break-all">
                  kenyachallenge.org{APK.pinned_url}
                </a>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* iPhone fallback */}
      <p className="text-center text-sm text-stone-500 pt-4 border-t border-stone-100" data-testid="android-iphone-fallback">
        On iPhone?{' '}
        <a
          href="https://apps.apple.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium"
        >
          <Apple className="w-3.5 h-3.5" /> Get it on the App Store
        </a>{' '}
        <span className="text-stone-400">(coming soon)</span>
      </p>
    </div>
  );
}

function Step({ number, title, body }) {
  return (
    <li className="flex gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 text-orange-700 font-bold flex items-center justify-center text-sm">
        {number}
      </div>
      <div className="min-w-0 pt-0.5">
        <p className="font-semibold text-stone-900 text-sm">{title}</p>
        <p className="text-sm text-stone-600 mt-1 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}
